// Chakra imports
import { 
  Box, 
  Grid, 
  useToast, 
  Button, 
  Flex, 
  useColorModeValue,
  VStack,
  Switch,
  Badge,
} from "@chakra-ui/react";

// Custom components
import Banner from "../../views/admin/profile/components/Banner";
import EditableInformation from "../../views/admin/profile/components/EditableInformation";
import Card from "../../components/card/Card";

// Assets
import React, { useEffect, useState } from 'react';
import banner from "../../assets/img/auth/banner.png";
import avatar from "../../assets/img/avatars/avatar4.png";
import { useParams } from 'react-router-dom';
import { getData, putData } from "../../utils/httpClient";
import { Text } from "@chakra-ui/react";

/** ISO o string fecha → YYYY-MM-DD para input type="date" */
function toDateInputValue(v) {
  if (v == null || v === '') return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function formatDateDisplay(v) {
  const iso = toDateInputValue(v);
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export default function EmployeeProfile() {
  const { id } = useParams();
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const toast = useToast();
  const buttonColorScheme = useColorModeValue("brand", "brand");

  

  const getEmployee = async () => {
    try {
      setInitialLoading(true);
      // Primero intentar obtener como empleado
      let response;
      let employeeInfo;
      let isPerson = false;
      
      try {
        response = await getData(`/employees/${id}`);
        employeeInfo = response?.payload?.employee || response || {};
        console.log('Employee response:', response);
      } catch (employeeError) {
        // Si falla, intentar obtener como persona (docente)
        try {
          response = await getData(`/person/findById/${id}`);
          employeeInfo = response?.payload?.person || {};
          isPerson = true;
          console.log('Person response (teacher):', response);
        } catch (personError) {
          throw new Error('No se pudo obtener la información');
        }
      }
      
      console.log('Final info:', employeeInfo);
      console.log('Photo data exists:', !!employeeInfo.photo);
      console.log('Photo data length:', employeeInfo.photo ? employeeInfo.photo.length : 0);
      setEmployeeData(employeeInfo);
      
      // Preparar datos editables combinando person y employee
      const combinedData = {
        // Datos de persona
        name: isPerson ? employeeInfo.name : (employeeInfo.person?.name || ''),
        lastName: isPerson ? employeeInfo.lastName : (employeeInfo.person?.lastName || ''),
        document: isPerson ? employeeInfo.document : (employeeInfo.person?.document || ''),
        email: isPerson ? employeeInfo.email : (employeeInfo.person?.email || ''),
        phone: isPerson ? employeeInfo.phone : (employeeInfo.person?.phone || ''),
        cellPhone: isPerson ? employeeInfo.cellPhone : (employeeInfo.person?.cellPhone || ''),
        birthPlace: isPerson ? employeeInfo.birthPlace : (employeeInfo.person?.birthPlace || ''),
        birthDate: isPerson ? employeeInfo.birthDate : (employeeInfo.person?.birthDate || ''),
        profession: isPerson ? employeeInfo.profession : (employeeInfo.person?.profession || ''),
        photo: isPerson ? employeeInfo.photo : (employeeInfo.person?.photo || ''),
        // Datos de empleado (solo si no es persona)
        employeeTypeId: isPerson ? '' : (employeeInfo.employeeTypeId || ''),
        employeeTitle: isPerson ? '' : (employeeInfo.employeeTitle || ''),
        position: isPerson ? '' : (employeeInfo.position || ''),
        department: isPerson ? '' : (employeeInfo.department || ''),
        salary: isPerson ? '' : (employeeInfo.salary || ''),
        hireDate: isPerson ? '' : toDateInputValue(employeeInfo.hireDate),
        startDate: isPerson ? '' : toDateInputValue(employeeInfo.startDate),
        endDate: isPerson ? '' : toDateInputValue(employeeInfo.endDate),
        terminationDate: isPerson ? '' : toDateInputValue(employeeInfo.terminationDate),
        terminationReason: isPerson ? '' : (employeeInfo.terminationReason || ''),
        workSchedule: isPerson ? '' : (employeeInfo.workSchedule || ''),
        overtimeRate: isPerson ? '' : (employeeInfo.overtimeRate || ''),
        bonusPercentage: isPerson ? '' : (employeeInfo.bonusPercentage || ''),
        hasHealthInsurance: isPerson ? false : (employeeInfo.hasHealthInsurance || false),
        hasPensionPlan: isPerson ? false : (employeeInfo.hasPensionPlan || false),
        emergencyContact: isPerson ? '' : (employeeInfo.emergencyContact || ''),
        emergencyPhone: isPerson ? '' : (employeeInfo.emergencyPhone || ''),
        notes: isPerson ? '' : (employeeInfo.notes || ''),
        // Flag para identificar el tipo
        isPerson: isPerson
      };
      
      console.log('Combined data:', combinedData);
      console.log('Combined photo exists:', !!combinedData.photo);
      setEditedData(combinedData);
    } catch (error) {
      toast({
        title: 'Error al obtener información',
        description: 'No se pudo obtener la información del empleado/docente',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const getEmployeeTypes = async () => {
    try {
      const response = await getData('/employeeTypes');
      setEmployeeTypes(response?.payload?.employeeTypes || []);
    } catch (error) {
      console.error('Error fetching employee types:', error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    const isPerson = editedData.isPerson;
    setEditedData({
      // Restaurar datos originales
      name: isPerson ? employeeData.name : (employeeData.person?.name || ''),
      lastName: isPerson ? employeeData.lastName : (employeeData.person?.lastName || ''),
      document: isPerson ? employeeData.document : (employeeData.person?.document || ''),
      email: isPerson ? employeeData.email : (employeeData.person?.email || ''),
      phone: isPerson ? employeeData.phone : (employeeData.person?.phone || ''),
      cellPhone: isPerson ? employeeData.cellPhone : (employeeData.person?.cellPhone || ''),
      birthPlace: isPerson ? employeeData.birthPlace : (employeeData.person?.birthPlace || ''),
      birthDate: isPerson ? employeeData.birthDate : (employeeData.person?.birthDate || ''),
      profession: isPerson ? employeeData.profession : (employeeData.person?.profession || ''),
      photo: isPerson ? employeeData.photo : (employeeData.person?.photo || ''),
      employeeTypeId: isPerson ? '' : (employeeData.employeeTypeId || ''),
      employeeTitle: isPerson ? '' : (employeeData.employeeTitle || ''),
      position: isPerson ? '' : (employeeData.position || ''),
      department: isPerson ? '' : (employeeData.department || ''),
      salary: isPerson ? '' : (employeeData.salary || ''),
      hireDate: isPerson ? '' : toDateInputValue(employeeData.hireDate),
      startDate: isPerson ? '' : toDateInputValue(employeeData.startDate),
      endDate: isPerson ? '' : toDateInputValue(employeeData.endDate),
      terminationDate: isPerson ? '' : toDateInputValue(employeeData.terminationDate),
      terminationReason: isPerson ? '' : (employeeData.terminationReason || ''),
      workSchedule: isPerson ? '' : (employeeData.workSchedule || ''),
      overtimeRate: isPerson ? '' : (employeeData.overtimeRate || ''),
      bonusPercentage: isPerson ? '' : (employeeData.bonusPercentage || ''),
      hasHealthInsurance: isPerson ? false : (employeeData.hasHealthInsurance || false),
      hasPensionPlan: isPerson ? false : (employeeData.hasPensionPlan || false),
      emergencyContact: isPerson ? '' : (employeeData.emergencyContact || ''),
      emergencyPhone: isPerson ? '' : (employeeData.emergencyPhone || ''),
      notes: isPerson ? '' : (employeeData.notes || ''),
      isPerson: isPerson
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const isPerson = editedData.isPerson;
      
      // Separar datos de persona y empleado para las APIs
      const personData = {
        name: editedData.name,
        lastName: editedData.lastName,
        document: editedData.document,
        email: editedData.email,
        phone: editedData.phone,
        cellPhone: editedData.cellPhone,
        birthPlace: editedData.birthPlace,
        birthDate: editedData.birthDate,
        profession: editedData.profession,
        photo: editedData.photo
      };

      // Limpiar campos vacíos de persona
      Object.keys(personData).forEach(key => {
        if (personData[key] === '' || personData[key] === null || personData[key] === undefined) {
          delete personData[key];
        }
      });

      if (isPerson) {
        // Si es una persona (docente), solo actualizar datos de persona
        await putData(`/person/${id}`, personData);
      } else {
        // Si es un empleado, actualizar tanto persona como empleado
        const employeeDataToUpdate = {
          employeeTypeId: editedData.employeeTypeId,
          employeeTitle: editedData.employeeTitle,
          position: editedData.position,
          department: editedData.department,
          salary: parseFloat(editedData.salary) || 0,
          hireDate: editedData.hireDate ? editedData.hireDate : null,
          startDate: editedData.startDate ? editedData.startDate : null,
          endDate: editedData.endDate ? editedData.endDate : null,
          terminationDate: editedData.terminationDate ? editedData.terminationDate : null,
          terminationReason:
            editedData.terminationReason && String(editedData.terminationReason).trim()
              ? String(editedData.terminationReason).trim()
              : null,
          workSchedule: editedData.workSchedule,
          overtimeRate: parseFloat(editedData.overtimeRate) || 0,
          bonusPercentage: parseFloat(editedData.bonusPercentage) || 0,
          hasHealthInsurance: editedData.hasHealthInsurance,
          hasPensionPlan: editedData.hasPensionPlan,
          emergencyContact: editedData.emergencyContact,
          emergencyPhone: editedData.emergencyPhone,
          notes: editedData.notes
        };

        // Quitar solo undefined y strings vacíos; conservar null (borra fechas/motivo en backend)
        Object.keys(employeeDataToUpdate).forEach(key => {
          const v = employeeDataToUpdate[key];
          if (v === '' || v === undefined) {
            delete employeeDataToUpdate[key];
          }
        });

        // Actualizar datos
        const personId = employeeData?.person?.id;
        if (!personId) {
          throw new Error('No se encontró la persona asociada al empleado');
        }
        await putData(`/person/${personId}`, { ...personData, updatedBy: 1 });
        await putData(`/employees/${id}`, employeeDataToUpdate);
      }
      
      // Recargar datos
      await getEmployee();
      setIsEditing(false);
      
      toast({
        title: 'Perfil actualizado',
        description: `Los datos del ${isPerson ? 'docente' : 'empleado'} han sido actualizados exitosamente`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error al actualizar:', error);
      toast({
        title: 'Error al actualizar',
        description: error.response?.data?.message || 'Hubo un problema al actualizar los datos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoChange = (photoBase64) => {
    setEditedData(prev => ({
      ...prev,
      photo: photoBase64
    }));
  };
  
  useEffect(() => {
    getEmployee();
    getEmployeeTypes();
  }, [id]);
  
  if (initialLoading) return null;
  
  if (!employeeData) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Card maxW="4xl" mx="auto" p={8} borderRadius="lg" boxShadow="xl">
          <VStack spacing={4} align="center">
            <Text>No se encontró información del empleado</Text>
            <Button colorScheme="blue" onClick={() => window.history.back()}>
              Volver
            </Button>
          </VStack>
        </Card>
      </Box>
    );
  }

  const isPerson = editedData?.isPerson;
  let avatarSource = avatar; // default avatar
  
  if (isEditing && editedData?.photo) {
    avatarSource = editedData.photo;
  } else if (isPerson && employeeData?.photo) {
    avatarSource = employeeData.photo;
  } else if (!isPerson && employeeData?.person?.photo) {
    avatarSource = employeeData.person.photo;
  } else if (employeeData?.photo) {
    avatarSource = employeeData.photo;
  }
  
  console.log('Avatar debug:', {
    isEditing,
    isPerson,
    editedDataPhoto: editedData?.photo ? 'HAS_PHOTO' : 'NO_PHOTO',
    employeeDataPhoto: employeeData?.photo ? 'HAS_PHOTO' : 'NO_PHOTO',
    personPhoto: employeeData?.person?.photo ? 'HAS_PHOTO' : 'NO_PHOTO',
    finalAvatar: avatarSource === avatar ? 'DEFAULT_AVATAR' : 'CUSTOM_PHOTO'
  });

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Botones de edición */}
      <Flex justify="flex-end" mb="20px">
        {!isEditing ? (
          <Button
            colorScheme={buttonColorScheme}
            onClick={handleEdit}
            size="md"
          >
            Editar Perfil
          </Button>
        ) : (
          <Flex gap="10px">
            <Button
              variant="outline"
              onClick={handleCancel}
              size="md"
            >
              Cancelar
            </Button>
            <Button
              colorScheme={buttonColorScheme}
              onClick={handleSave}
              size="md"
            >
              Guardar Cambios
            </Button>
          </Flex>
        )}
      </Flex>

      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1.34fr 2.62fr",
        }}
        templateRows={{
          base: "repeat(1fr)",
          lg: "1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}>
        <Banner
          gridArea='1 / 1 / 2 / 2'
          banner={banner}
          avatar={avatarSource}
          name={
            editedData?.isPerson 
              ? `${employeeData?.name || ''} ${employeeData?.lastName || ''}`
              : `${employeeData?.person?.name || employeeData?.name || ''} ${employeeData?.person?.lastName || employeeData?.lastName || ''}`
          }
          job={
            editedData?.isPerson 
              ? (employeeData?.profession || 'Docente')
              : (employeeData?.employeeType?.description || 'Empleado')
          }
          posts=''
          followers=''
          following=''
          isEditing={isEditing}
          onPhotoChange={handlePhotoChange}
        />
        <PersonalDataEmployee
          gridArea='1'
          employeeData={isEditing ? editedData : employeeData}
          isEditing={isEditing}
          onFieldChange={handleFieldChange}
        />
      </Grid>
      
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1fr 1fr",
        }}
        templateRows={{
          base: "repeat(3, 1fr)",
          lg: "1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
        
        {/* Solo mostrar datos laborales si NO es una persona (es empleado) */}
        {!editedData?.isPerson && (
          <WorkDataEmployee
            gridArea='1'
            employeeData={isEditing ? editedData : employeeData}
            employeeTypes={employeeTypes}
            isEditing={isEditing}
            onFieldChange={handleFieldChange}
          />
        )}
        
        <ContactDataEmployee
          gridArea='1'
          employeeData={isEditing ? editedData : employeeData}
          isEditing={isEditing}
          onFieldChange={handleFieldChange}
        />
        
        {/* Solo mostrar beneficios si NO es una persona */}
        {!editedData?.isPerson && (
          <BenefitsDataEmployee
            gridArea='1'
            employeeData={isEditing ? editedData : employeeData}
            isEditing={isEditing}
            onFieldChange={handleFieldChange}
          />
        )}
        
        {/* Solo mostrar notas y horarios si NO es una persona */}
        {!editedData?.isPerson && (
          <NotesDataEmployee
            gridArea='1'
            employeeData={isEditing ? editedData : employeeData}
            isEditing={isEditing}
            onFieldChange={handleFieldChange}
          />
        )}
        
        {/* Para personas (docentes), mostrar información adicional */}
        {editedData?.isPerson && (
          <TeacherDataEmployee
            gridArea='1'
            employeeData={isEditing ? editedData : employeeData}
            isEditing={isEditing}
            onFieldChange={handleFieldChange}
          />
        )}
      </Grid>
    </Box>
  );
}

// Componente para datos personales del empleado
function PersonalDataEmployee({ employeeData, isEditing, onFieldChange }) {
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');

  return (
    <Card mb={{ base: '0px', '2xl': '20px' }}>
      <Text
        color={textColorPrimary}
        fontWeight="bold"
        fontSize="2xl"
        mt="10px"
        mb="4px"
      >
        Información Personal
      </Text>
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1fr 1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Nombre"
          value={isEditing ? employeeData?.name : (
            employeeData?.isPerson 
              ? employeeData?.name
              : (employeeData?.person?.name || employeeData?.name)
          )}
          isEditing={isEditing}
          field="name"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Apellido"
          value={isEditing ? employeeData?.lastName : (
            employeeData?.isPerson 
              ? employeeData?.lastName
              : (employeeData?.person?.lastName || employeeData?.lastName)
          )}
          isEditing={isEditing}
          field="lastName"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Documento"
          value={isEditing ? employeeData?.document : (
            employeeData?.isPerson 
              ? employeeData?.document
              : (employeeData?.person?.document || employeeData?.document)
          )}
          isEditing={isEditing}
          field="document"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Email"
          value={isEditing ? employeeData?.email : (
            employeeData?.isPerson 
              ? employeeData?.email
              : (employeeData?.person?.email || employeeData?.email)
          )}
          isEditing={isEditing}
          field="email"
          type="email"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Fecha de nacimiento"
          value={isEditing ? employeeData?.birthDate : (
            employeeData?.isPerson 
              ? employeeData?.birthDate
              : (employeeData?.person?.birthDate || employeeData?.birthDate)
          )}
          isEditing={isEditing}
          field="birthDate"
          type="date"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Lugar de nacimiento"
          value={isEditing ? employeeData?.birthPlace : (
            employeeData?.isPerson 
              ? employeeData?.birthPlace
              : (employeeData?.person?.birthPlace || employeeData?.birthPlace)
          )}
          isEditing={isEditing}
          field="birthPlace"
          onFieldChange={onFieldChange}
        />
      </Grid>
    </Card>
  );
}

// Componente para datos laborales
function WorkDataEmployee({ employeeData, employeeTypes, isEditing, onFieldChange }) {
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');

  const employeeTypeOptions = employeeTypes.map(type => ({
    value: type.id,
    label: type.description
  }));

  return (
    <Card mb={{ base: '0px', '2xl': '20px' }}>
      <Text
        color={textColorPrimary}
        fontWeight="bold"
        fontSize="2xl"
        mt="10px"
        mb="4px"
      >
        Información Laboral
      </Text>
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1fr 1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Tipo de Empleado"
          value={isEditing ? employeeData?.employeeTypeId : (employeeData?.employeeType?.description || 'Sin información')}
          isEditing={isEditing}
          field="employeeTypeId"
          type="select"
          options={employeeTypeOptions}
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Título/Cargo"
          value={employeeData?.employeeTitle}
          isEditing={isEditing}
          field="employeeTitle"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Posición"
          value={employeeData?.position}
          isEditing={isEditing}
          field="position"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Departamento"
          value={employeeData?.department}
          isEditing={isEditing}
          field="department"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Salario"
          value={employeeData?.salary}
          isEditing={isEditing}
          field="salary"
          type="number"
          onFieldChange={onFieldChange}
        />

        <EditableInformation
          boxShadow={cardShadow}
          title="Fecha de ingreso"
          value={
            isEditing
              ? (employeeData?.startDate || '')
              : formatDateDisplay(employeeData?.startDate)
          }
          isEditing={isEditing}
          field="startDate"
          type="date"
          onFieldChange={onFieldChange}
        />

        <EditableInformation
          boxShadow={cardShadow}
          title="Fecha de contratación"
          value={
            isEditing
              ? (employeeData?.hireDate || '')
              : formatDateDisplay(employeeData?.hireDate)
          }
          isEditing={isEditing}
          field="hireDate"
          type="date"
          onFieldChange={onFieldChange}
        />

        <EditableInformation
          boxShadow={cardShadow}
          title="Fecha de egreso"
          value={
            isEditing
              ? (employeeData?.terminationDate || '')
              : formatDateDisplay(employeeData?.terminationDate)
          }
          isEditing={isEditing}
          field="terminationDate"
          type="date"
          onFieldChange={onFieldChange}
        />

        <EditableInformation
          boxShadow={cardShadow}
          title="Motivo de egreso"
          value={employeeData?.terminationReason || ''}
          isEditing={isEditing}
          field="terminationReason"
          type="textarea"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Profesión"
          value={isEditing ? employeeData?.profession : (
            employeeData?.isPerson 
              ? employeeData?.profession
              : (employeeData?.person?.profession || employeeData?.profession)
          )}
          isEditing={isEditing}
          field="profession"
          onFieldChange={onFieldChange}
        />
      </Grid>
    </Card>
  );
}

// Componente para datos de contacto
function ContactDataEmployee({ employeeData, isEditing, onFieldChange }) {
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');

  return (
    <Card mb={{ base: '0px', '2xl': '20px' }}>
      <Text
        color={textColorPrimary}
        fontWeight="bold"
        fontSize="2xl"
        mt="10px"
        mb="4px"
      >
        Información de Contacto
      </Text>
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1fr 1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Teléfono"
          value={isEditing ? employeeData?.phone : (
            employeeData?.isPerson 
              ? employeeData?.phone
              : (employeeData?.person?.phone || employeeData?.phone)
          )}
          isEditing={isEditing}
          field="phone"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Celular"
          value={isEditing ? employeeData?.cellPhone : (
            employeeData?.isPerson 
              ? employeeData?.cellPhone
              : (employeeData?.person?.cellPhone || employeeData?.cellPhone)
          )}
          isEditing={isEditing}
          field="cellPhone"
          onFieldChange={onFieldChange}
        />
        
        {/* Solo mostrar contacto de emergencia para empleados */}
        {!employeeData?.isPerson && (
          <>
            <EditableInformation
              boxShadow={cardShadow}
              title="Contacto de Emergencia"
              value={employeeData?.emergencyContact}
              isEditing={isEditing}
              field="emergencyContact"
              onFieldChange={onFieldChange}
            />
            
            <EditableInformation
              boxShadow={cardShadow}
              title="Teléfono de Emergencia"
              value={employeeData?.emergencyPhone}
              isEditing={isEditing}
              field="emergencyPhone"
              onFieldChange={onFieldChange}
            />
          </>
        )}
      </Grid>
    </Card>
  );
}

// Componente para beneficios
function BenefitsDataEmployee({ employeeData, isEditing, onFieldChange }) {
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');

  return (
    <Card mb={{ base: '0px', '2xl': '20px' }}>
      <Text
        color={textColorPrimary}
        fontWeight="bold"
        fontSize="2xl"
        mt="10px"
        mb="4px"
      >
        Beneficios y Condiciones
      </Text>
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1fr 1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Tarifa Horas Extra (%)"
          value={employeeData?.overtimeRate}
          isEditing={isEditing}
          field="overtimeRate"
          type="number"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Bonificación (%)"
          value={employeeData?.bonusPercentage}
          isEditing={isEditing}
          field="bonusPercentage"
          type="number"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Seguro Médico"
          value={employeeData?.hasHealthInsurance ? 'Sí' : 'No'}
          isEditing={isEditing}
          field="hasHealthInsurance"
          type="select"
          options={[
            { value: true, label: 'Sí' },
            { value: false, label: 'No' }
          ]}
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Plan de Pensión"
          value={employeeData?.hasPensionPlan ? 'Sí' : 'No'}
          isEditing={isEditing}
          field="hasPensionPlan"
          type="select"
          options={[
            { value: true, label: 'Sí' },
            { value: false, label: 'No' }
          ]}
          onFieldChange={onFieldChange}
        />
      </Grid>
    </Card>
  );
}

// Componente para observaciones
function NotesDataEmployee({ employeeData, isEditing, onFieldChange }) {
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');

  return (
    <Card mb={{ base: '0px', '2xl': '20px' }}>
      <Text
        color={textColorPrimary}
        fontWeight="bold"
        fontSize="2xl"
        mt="10px"
        mb="4px"
      >
        Observaciones y Horarios
      </Text>
      <Grid
        templateColumns="1fr"
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Horario de Trabajo"
          value={employeeData?.workSchedule}
          isEditing={isEditing}
          field="workSchedule"
          type="textarea"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Observaciones"
          value={employeeData?.notes}
          isEditing={isEditing}
          field="notes"
          type="textarea"
          onFieldChange={onFieldChange}
        />
      </Grid>
    </Card>
  );
}

// Componente específico para datos de docentes
function TeacherDataEmployee({ employeeData, isEditing, onFieldChange }) {
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = 'gray.400';
  const cardShadow = useColorModeValue('0px 18px 40px rgba(112, 144, 176, 0.12)', 'unset');
  const toast = useToast();

  // "Estado" Activo/Inactivo del docente (Módulo 2).
  //
  // En este flujo (isPerson=true) el componente recibe la Person como
  // employeeData. El estado Activo/Inactivo del docente se guarda en
  // person.employee.enabled (tinyint 1/0). Si aún no existiera la relación
  // employee, se considera "Activo" por defecto (no es bloqueante para la UI).
  //
  // Persistencia: se usa PUT /person/update/:id con { employee: { enabled } },
  // que es el endpoint que el backend ya implementa para actualizar el flag.
  // Así evitamos depender de /employees/:id/activate, que requiere el id del
  // Employee (no el id de la Person disponible en la URL del perfil).
  const currentEnabled =
    employeeData?.employee?.enabled === false ||
    employeeData?.employee?.enabled === 0
      ? false
      : true;

  const [isActive, setIsActive] = useState(currentEnabled);
  const [savingStatus, setSavingStatus] = useState(false);

  useEffect(() => {
    setIsActive(currentEnabled);
  }, [currentEnabled]);

  const personId = employeeData?.id;

  const handleToggleEnabled = async (nextValue) => {
    if (!personId) {
      toast({
        title: 'No se puede actualizar el estado',
        description: 'Falta el ID del docente.',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    setSavingStatus(true);
    const previous = isActive;
    setIsActive(nextValue);
    try {
      await putData(`/person/update/${personId}`, {
        employee: { enabled: nextValue },
      });

      if (onFieldChange) {
        onFieldChange('employee', {
          ...employeeData?.employee,
          enabled: nextValue,
        });
      }

      toast({
        title: nextValue ? 'Docente activado' : 'Docente inactivado',
        status: 'success',
        duration: 2500,
      });
    } catch (error) {
      setIsActive(previous);
      toast({
        title: 'No se pudo actualizar el estado',
        description: error?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 3500,
      });
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <Card mb={{ base: '0px', '2xl': '20px' }}>
      <Flex
        justify="space-between"
        align="center"
        mt="10px"
        mb="4px"
        wrap="wrap"
        gap={3}
      >
        <Text color={textColorPrimary} fontWeight="bold" fontSize="2xl">
          Información del Docente
        </Text>

        {/*
          Toggle de Estado (Activo/Inactivo) del docente (Módulo 2).
          Siempre editable (no depende del modo "Editar perfil").
        */}
        <Flex align="center" gap={3}>
          <Badge
            colorScheme={isActive ? 'green' : 'red'}
            fontSize="sm"
            px={3}
            py={1}
            borderRadius="full"
          >
            {isActive ? 'Activo' : 'Inactivo'}
          </Badge>
          <Text fontSize="sm" color={textColorSecondary}>
            Estado
          </Text>
          <Switch
            size="md"
            colorScheme="green"
            isChecked={isActive}
            isDisabled={savingStatus || !personId}
            onChange={(e) => handleToggleEnabled(e.target.checked)}
          />
        </Flex>
      </Flex>
      <Grid
        templateColumns={{
          base: "1fr",
          lg: "1fr 1fr",
        }}
        gap={{ base: "20px", xl: "20px" }}
        mt={{ base: "20px", lg: "20px" }}>
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Profesión"
          value={isEditing ? employeeData?.profession : employeeData?.profession}
          isEditing={isEditing}
          field="profession"
          onFieldChange={onFieldChange}
        />
        
        <EditableInformation
          boxShadow={cardShadow}
          title="Tipo"
          value="Docente"
          isEditing={false}
          field=""
        />
      </Grid>
    </Card>
  );
}
